// Pure logic behind the Disbursements screen (src/pages/DisbursementsTracker.jsx).
//
// A disbursement is an outside invoice on a client's matter that is not counsel's fee: a
// psychologist's or psychiatrist's report, a medical report, an interpreter, a transcript.
// Each supplier lays its invoice out differently, so they arrive as PDFs and are read here
// as best we can, then checked by a person before saving.
//
// Everything lives in one shared vals_drafts record per brand (form_type "disbursements"),
// the same way Recover from aid does (src/lib/budgetRecoup.js): `{ items: [...], ignored: [...] }`.
// `ignored` holds the emails and old Client Account rows someone marked "not a disbursement",
// so they stop being offered. Every
// change is a single delta that returns a new object, so that when a colleague saves first
// the same change can be re-applied to their newer record instead of overwriting it.

import { readInvoiceAmount } from "./invoiceAmount.js";
import { parseInvoiceText, toIsoDate } from "./budgetRecoup.js";
import { emailClientName } from "./emailIntake.js";
import { personName } from "./clerkEmail.js";
import { parseSplitAmount, partialSplit } from "./partialPaid.js";

export const DISBURSEMENT_FORM_TYPE = "disbursements";

// Fixed per brand so every user reads and writes the same record.
const DISBURSEMENT_DRAFT_IDS = {
  vals: "00000000-0000-4000-8000-00000000c3a0",
  lacw: "00000000-0000-4000-8000-00000000c3a1",
  rga: "00000000-0000-4000-8000-00000000c3a2",
  eas: "00000000-0000-4000-8000-00000000c3a3",
  admin: "00000000-0000-4000-8000-00000000c3a4",
};
export const disbursementDraftId = (brand) => DISBURSEMENT_DRAFT_IDS[brand] || DISBURSEMENT_DRAFT_IDS.lacw;

export const DISBURSEMENT_TYPES = [
  { value: "psychological_report", label: "Psychological report" },
  { value: "psychiatric_report", label: "Psychiatric report" },
  { value: "neuropsychological_report", label: "Neuropsychological report" },
  { value: "medical_report", label: "Medical report" },
  { value: "interpreter", label: "Interpreter" },
  { value: "transcript", label: "Transcript" },
  // A staff travel claim, sent from the Travel Claims form (src/lib/travelClaimDisbursements.js).
  { value: "travel", label: "Travel claim" },
  { value: "other", label: "Other" },
];
const TYPE_VALUES = DISBURSEMENT_TYPES.map((type) => type.value);

// The fee slip statuses that make sense for a supplier's invoice (src/lib/clerkStatements.js),
// with the same values and labels so the two screens read alike. The email statuses are
// left out: there is no clerk to email.
export const DISBURSEMENT_STATUSES = [
  { value: "not_paid", label: "Outstanding" },
  { value: "aid_pending", label: "Aid Pending" },
  { value: "retrospective_aid_required", label: "Retrospective Aid Required" },
  { value: "aid_not_applied_for", label: "Aid Not Applied For" },
  { value: "to_be_paid_from_budget", label: "To Be Paid From Budget" },
  // Emma, 27 Oct 2026: the claim is in ATLAS - set by hand.
  { value: "claim_submitted", label: "Claim submitted in ATLAS" },
  // Set when Email finance is sent (src/lib/disbursementEmail.js), shown with the date it went.
  // Was "Claimed - Finance Emailed"; the stored value is unchanged, so earlier sends read the
  // same way.
  { value: "finance_emailed", label: "Request sent to Finance to pay invoice" },
  { value: "claimed_to_provider", label: "Claimed to provider" },
  // Only VALS's page offers it.
  { value: "claimed_to_vals", label: "Claimed to VALS", brands: ["vals"] },
  { value: "partial_paid", label: "Partial Paid" },
  { value: "paid", label: "Paid" },
  { value: "other", label: "Other" },
];
const STATUS_VALUES = DISBURSEMENT_STATUSES.map((status) => status.value);
export const isOpenStatus = (status) => status !== "paid";

// Paid: marked Paid, or ticked "VALS to be reimbursed" - an invoice paid from the crim budget,
// outside ATLAS, that VALS will reimburse (Emma, 23 Oct 2026). A ticked invoice keeps its own
// status (Retrospective Aid Required, Aid Pending...), which still says something; it is only
// counted with the paid ones.
export const isPaidDisbursement = (item = {}) => item.status === "paid" || item.reimburse === true;

export const disbursementStatusLabel = (status) =>
  DISBURSEMENT_STATUSES.find((option) => option.value === status)?.label || "Outstanding";

// The statuses a firm's page offers ("Claimed to VALS" only on VALS's).
export const statusesFor = (brand) => DISBURSEMENT_STATUSES.filter((option) => !option.brands || option.brands.includes(brand));

// A status as shown on an invoice: "Request sent to Finance to pay invoice" carries the date
// it was sent.
export function disbursementStatusText(item = {}) {
  const label = disbursementStatusLabel(item.status);
  if (item.status !== "finance_emailed" || !item.financeEmailedAt) return label;
  const [y, m, d] = String(item.financeEmailedAt).slice(0, 10).split("-");
  return y && m && d ? `${label} ${d}/${m}/${y}` : label;
}

export function disbursementTypeLabel(item = {}) {
  if (item.type === "other") return text(item.typeOther, 60) || "Other";
  return DISBURSEMENT_TYPES.find((option) => option.value === item.type)?.label || "Other";
}

// "VALS to be reimbursed" means what it means on the Fee Slips screen: the firm paid this on
// VALS's behalf and expects it back. LACW has no such arrangement, so its screen hides it.
export const REIMBURSEMENT_LABEL = "VALS to be reimbursed";
export const showsReimbursement = (brand) => brand !== "lacw";

const text = (value, max = 300) => String(value ?? "").trim().slice(0, max);

export function parseAmount(value) {
  const amount = Number.parseFloat(String(value ?? "").replace(/[$,\s]/g, ""));
  return Number.isFinite(amount) && amount >= 0 && amount < 10000000 ? Math.round(amount * 100) / 100 : 0;
}

export const emptyDisbursementData = () => ({ items: [], ignored: [] });

// Stored data is treated as untrusted shape: an older or hand-edited record must not crash
// the screen. An unknown type or status falls back rather than dropping the invoice.
export function normaliseDisbursementData(formData) {
  const items = (Array.isArray(formData?.items) ? formData.items : [])
    .filter((item) => item && typeof item === "object" && item.id)
    .map((item) => ({
      ...item,
      type: TYPE_VALUES.includes(item.type) ? item.type : "other",
      status: STATUS_VALUES.includes(item.status) ? item.status : "not_paid",
      amount: parseAmount(item.amount),
      reimburse: item.reimburse === true,
      emailIds: Array.isArray(item.emailIds) ? item.emailIds.filter((id) => typeof id === "string") : [],
    }));
  const ignored = (Array.isArray(formData?.ignored) ? formData.ignored : []).filter((key) => typeof key === "string");
  return { items, ignored };
}

const FIELD_LIMITS = {
  typeOther: 60, supplier: 120, clientName: 120, invoiceRef: 60, invoiceDate: 10,
  description: 200, aidRef: 60, paidOn: 10, note: 500,
  fileName: 200, fileUrl: 500, filePath: 500, source: 20, legacyKey: 300,
  financeEmailedAt: 30, financeEmailedBy: 120, financeEmailedTo: 200,
  claimReceiptName: 200, claimReceiptUrl: 500, claimReceiptPath: 500,
};

export function buildDisbursement(fields = {}, meta = {}) {
  const item = {
    id: meta.id || `disb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    type: TYPE_VALUES.includes(fields.type) ? fields.type : "other",
    status: STATUS_VALUES.includes(fields.status) ? fields.status : "not_paid",
    amount: parseAmount(fields.amount),
    gst: parseAmount(fields.gst),
    amountPaid: parseAmount(fields.amountPaid),
    // Partial Paid: claimed from VLA, and what is left to pay from the budget (src/lib/partialPaid.js).
    vlaClaimed: parseSplitAmount(fields.vlaClaimed),
    budgetRemaining: parseSplitAmount(fields.budgetRemaining),
    reimburse: fields.reimburse === true,
    page: Number.isInteger(fields.page) && fields.page > 0 ? fields.page : null,
    // How many pages of a shared PDF are this invoice's, when more than one.
    pageCount: Number.isInteger(fields.pageCount) && fields.pageCount > 1 ? fields.pageCount : null,
    // The emailed items this invoice came from, so they are not offered again.
    emailIds: (Array.isArray(fields.emailIds) ? fields.emailIds : []).map(String).slice(0, 20),
    // A travel claim's expenses: what each is for, its fee and its receipts.
    travelLines: Array.isArray(fields.travelLines) ? fields.travelLines.slice(0, 30) : [],
    uploadedAt: meta.at || new Date().toISOString(),
    uploadedBy: text(meta.by, 120),
  };
  for (const [key, limit] of Object.entries(FIELD_LIMITS)) item[key] = text(fields[key], limit);
  return item;
}

export function addDisbursements(data, items = []) {
  const known = new Set(data.items.map((item) => item.id));
  const fresh = items.filter((item) => !known.has(item.id));
  return fresh.length ? { ...data, items: [...data.items, ...fresh] } : data;
}

// Corrects an invoice's details. Only the fields given change; it keeps who uploaded it and
// records who last changed it. A status change is dated, so the weekly summary can say which
// week it moved in.
export function updateDisbursement(data, id, fields = {}, meta = {}) {
  const at = meta.at || new Date().toISOString();
  return {
    ...data,
    items: data.items.map((item) => {
      if (item.id !== id) return item;
      const next = { ...item };
      for (const [key, limit] of Object.entries(FIELD_LIMITS)) {
        if (fields[key] !== undefined) next[key] = text(fields[key], limit);
      }
      for (const key of ["amount", "gst", "amountPaid"]) {
        if (fields[key] !== undefined) next[key] = parseAmount(fields[key]);
      }
      for (const key of ["vlaClaimed", "budgetRemaining"]) {
        if (fields[key] !== undefined) next[key] = parseSplitAmount(fields[key]);
      }
      if (fields.type !== undefined && TYPE_VALUES.includes(fields.type)) next.type = fields.type;
      if (fields.page !== undefined) next.page = Number.isInteger(fields.page) && fields.page > 0 ? fields.page : null;
      if (fields.status !== undefined && STATUS_VALUES.includes(fields.status) && fields.status !== item.status) {
        next.status = fields.status;
        next.statusAt = at;
        next.statusBy = text(meta.by, 120);
        // Marking it paid without a date means paid today.
        if (fields.status === "paid" && !next.paidOn && fields.paidOn === undefined) next.paidOn = at.slice(0, 10);
      }
      if (fields.reimburse !== undefined && (fields.reimburse === true) !== item.reimburse) {
        next.reimburse = fields.reimburse === true;
        next.reimburseAt = at;
        next.reimburseBy = text(meta.by, 120);
      }
      next.editedAt = at;
      next.editedBy = text(meta.by, 120);
      return next;
    }),
  };
}

export function removeDisbursement(data, id) {
  return { ...data, items: data.items.filter((item) => item.id !== id) };
}

// Stops (or, with on = false, restarts) offering an email or old Client Account row.
export function setIgnored(data, keys = [], on = true) {
  const current = new Set(data.ignored || []);
  for (const key of keys) {
    if (on) current.add(key);
    else current.delete(key);
  }
  return { ...data, ignored: [...current] };
}

const dupKey = (item) => {
  const ref = String(item.invoiceRef || "").replace(/\s+/g, "").toLowerCase();
  if (!ref) return "";
  const supplier = String(item.supplier || "").toLowerCase().replace(/[^a-z]/g, "").slice(0, 12);
  return `${supplier}|${ref}`;
};

// The invoice already on the list with the same supplier and invoice number, if any, so an
// invoice sent twice is not entered (and paid) twice.
export function findDuplicate(items = [], candidate = {}) {
  const key = dupKey(candidate);
  return key ? items.find((item) => item.id !== candidate.id && dupKey(item) === key) || null : null;
}

// What is still to pay. A part-paid invoice owes what is left to pay from the budget, or,
// before that split was kept, its total less what had been paid.
export function amountOwing(item = {}) {
  const total = parseAmount(item.amount);
  if (isPaidDisbursement(item)) return 0;
  if (item.status !== "partial_paid") return total;
  const split = partialSplit(total, item.vlaClaimed, item.budgetRemaining);
  if (split.fromBudget !== null) return split.fromBudget;
  return Math.max(0, total - parseAmount(item.amountPaid));
}

export function summariseDisbursements(items = []) {
  const sum = (list) => Math.round(list.reduce((total, item) => total + parseAmount(item.amount), 0) * 100) / 100;
  const open = items.filter((item) => !isPaidDisbursement(item));
  const owing = Math.round(open.reduce((total, item) => total + amountOwing(item), 0) * 100) / 100;
  const paid = items.filter(isPaidDisbursement);
  const reimburse = items.filter((item) => item.reimburse);
  return {
    open: { count: open.length, total: owing },
    paid: { count: paid.length, total: sum(paid) },
    reimburse: { count: reimburse.length, total: sum(reimburse) },
    all: { count: items.length, total: sum(items) },
    statuses: DISBURSEMENT_STATUSES.map((option) => {
      const list = items.filter((item) => item.status === option.value);
      return { ...option, count: list.length, total: sum(list) };
    }),
  };
}

// ---- Reading an invoice PDF ---------------------------------------------------------------
//
// pdf.js often gives a label AFTER its value. A report writer's invoice comes out as
//   "14/05/2025" "Date" "5753" "Invoice No:" ... "A Client" "Client Details"
// so each field is looked for both ways. Anything not found is left blank for a person to
// type: a blank is safer than a wrong guess. Nothing is sent anywhere to be read.
//
// The layouts this has been taught (22 Oct 2026, from the providers' real invoices):
//   - one invoice per page, labels after values (Warren Simmons)
//   - Xero "Tax Invoice": "Reference" holds "Surname, First" or "First Surname REF: 499845",
//     the invoice number can split over lines ("INV" / "7731"), and the business name over
//     two lines ("HEALEY & CIDONI" / "PSYCHOLOGISTS") - or in full after "payable to"
//   - Xero "Payment advice": the client is named in the description ("Assessment for Name")
//   - Xero with the client in the bill-to line: "... Legal Service_(client Name)"
//   - "INVOICE NO. 260904-JM1" / "DATE 04/09/26" inline, client under "REFERENCE"
//   - a typed letter: "October 5, 2026", "in relation to Mr. Name", "(Your ref: 502986)"

const FIRM_NAMES = /victorian aboriginal legal service|law (?:and|&) advocacy centre for women|eas legal|robyn greensill|complete law support/i;

// Lines that are a heading or a label, never a name or a description.
const LABEL_LINE = /^(?:tax\s+)?(?:invoice(?:\s+(?:no\.?|number|to|date))?|date|client(?:\s+(?:details|name))?|patient|total(?:\s+(?:amount|aud))?|total includes gst of|remittance advice|payment advice|account|description|gst|tax|price|amount(?:\s+aud)?|amt payable|eft details|bill to|attention|qty|quantity|rate|unit price|balance due|amount due|amount enclosed|due date|issue date|subtotal|reference|customer|view online|page \d+.*)\s*[:.]?$/i;

const MONTH_FIRST = /^([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/;
const DATE_LINE = /^(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{1,2}(?:st|nd|rd|th)?\s+[A-Za-z]{3,9}\.?,?\s+\d{4}|[A-Za-z]{3,9}\.?\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4})$/;
const MONEY_LINE = /^\$?\s*\d[\d,]*(?:\.\d+)?%?$/;
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

// "October 5, 2026" as well as everything toIsoDate already reads.
function isoDate(value = "") {
  const raw = String(value).trim();
  const monthFirst = raw.match(MONTH_FIRST);
  if (monthFirst) {
    const month = MONTHS.indexOf(monthFirst[1].slice(0, 3).toLowerCase());
    if (month < 0) return "";
    return `${monthFirst[3]}-${String(month + 1).padStart(2, "0")}-${monthFirst[2].padStart(2, "0")}`;
  }
  return toIsoDate(raw);
}

// pdf.js leaves stray symbol-font characters and spacer cells as lines of their own.
const cleanLines = (raw) => String(raw || "").split(/\r?\n/)
  .map((line) => line.replace(/[\u0000-\u001f\u007f-\u009f-]/g, "").replace(/\s+/g, " ").trim())
  .filter((line) => /[A-Za-z0-9$]/.test(line));

const isNameLike = (line) =>
  /[A-Za-z]{2}/.test(line)
  && line.length <= 60
  && !LABEL_LINE.test(line)
  && !FIRM_NAMES.test(line)
  && !/\d{3,}/.test(line)
  && !/^(?:po box|tel|ph|phone|fax|email|abn|acn|bsb|acct|www\.|http)/i.test(line)
  && !/@/.test(line);

// The value next to a label, whichever side pdf.js put it on. `bareInline` also reads a value
// that follows its label with no colon ("DATE 04/09/26").
function nextTo(lines, labelPattern, accept, { bareInline = false } = {}) {
  const separator = bareInline ? "\\s*[:#.-]?\\s*" : "\\s*[:#.-]\\s*";
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const inline = line.match(new RegExp(`^(?:${labelPattern.source})${separator}(.+)$`, "i"));
    if (inline && accept(inline[1].trim())) return inline[1].trim();
    if (!new RegExp(`^(?:${labelPattern.source})\\s*[:#.]?$`, "i").test(line)) continue;
    if (lines[i + 1] && accept(lines[i + 1])) return lines[i + 1];
    if (lines[i - 1] && accept(lines[i - 1])) return lines[i - 1];
  }
  return "";
}

// ---- The client ----

const TITLE = /^(?:mr|mrs|ms|miss|mx|dr|master)\.?\s+/i;
const NAME_WORD = "[A-Z][A-Za-z'’-]+";

// "MUSHINS, Jarrod" -> "Jarrod Mushins"; "Mr. Thomas Morgan" -> "Thomas Morgan".
const tidyName = (value) => personName(String(value || "").replace(TITLE, "").replace(/[\s,.;:]+$/, "").trim())
  .replace(/\b([A-Z])([A-Z'’-]+)\b/g, (_, first, rest) => first + rest.toLowerCase());

// A person's name and nothing else: two to four capitalised words, or "Surname, First".
function looksLikeName(value) {
  const text = String(value || "").replace(TITLE, "").replace(/[\s,.;:]+$/, "").trim();
  if (!text || text.length > 50 || /\d/.test(text) || FIRM_NAMES.test(text) || LABEL_LINE.test(text)) return false;
  if (new RegExp(`^${NAME_WORD},\\s*${NAME_WORD}(?:\\s+${NAME_WORD})?$`).test(text)) return true;
  const words = text.split(/\s+/);
  return words.length >= 2 && words.length <= 4 && words.every((word) => /^[A-Z][A-Za-z'’.-]*$/.test(word));
}

function readClient(lines) {
  const flat = lines.join(" ");
  // "... Legal Service_(client Joel Example)"
  const bracketed = flat.match(/\(\s*client[:\s]+([^)]{3,50})\)/i);
  if (bracketed && looksLikeName(bracketed[1])) return tidyName(bracketed[1]);

  const labelled = nextTo(lines, /client(?:\s+(?:details|name))?|patient(?:\s+name)?|name of client|re/, isNameLike);
  if (labelled) return labelled;

  // A typed letter: "... preparation of Court report in relation to Mr. Name".
  // Name words are matched case-sensitively, so a name stops at the first lowercase word.
  const relation = flat.match(new RegExp(`[Ii]n relation to\\s+((?:(?:Mr|Mrs|Ms|Miss|Mx|Master)\\.?\\s+)?${NAME_WORD}(?:\\s+${NAME_WORD}){1,3})`));
  if (relation && looksLikeName(relation[1])) return tidyName(relation[1]);

  // The service names the client: "Forensic Assessment for First Last including ...".
  const named = flat.match(new RegExp(`\\b(?:[Aa]ssessment|[Rr]eport|[Ii]nterview|[Cc]onsultation|[Ee]valuation)\\s+(?:for|of)\\s+((?:(?:Mr|Mrs|Ms|Miss|Mx)\\.?\\s+)?${NAME_WORD}(?:\\s+${NAME_WORD}){1,2})`));
  if (named && looksLikeName(named[1]) && !/^dr\b/i.test(named[1])) return tidyName(named[1]);

  // Xero's "Reference": "Surname, First", or "First Surname REF: 499845".
  const reference = nextTo(lines, /reference|your ref(?:erence)?/, (value) => looksLikeName(value.replace(/\s+ref\b.*$/i, "")));
  if (reference) return tidyName(reference.replace(/\s+ref\b.*$/i, ""));
  return "";
}

// The firm's own file or aid number when the provider quotes it: "REF: 499845",
// "(Your ref: 502986)".
function readAidRef(lines) {
  const found = lines.join(" ").match(/\b(?:your\s+ref(?:erence)?|ref)\s*[:.]?\s*(\d{2}[A-Z]\d{5,}(?:[-/]\d+)?|\d{4,})\b/i);
  return found ? found[1] : "";
}

// ---- The invoice number and date ----

function readInvoiceRef(lines) {
  const label = /(?:tax\s+)?invoice\s*(?:no\.?|number|num|#)|inv\s*(?:no\.?|#)/;
  // Xero sometimes splits the number from its prefix: "Invoice number" / "INV" / "7731".
  for (let i = 0; i < lines.length - 2; i += 1) {
    if (new RegExp(`^(?:${label.source})\\s*[:#.]?$`, "i").test(lines[i]) && /^INV$/i.test(lines[i + 1]) && /^\d{2,}$/.test(lines[i + 2])) {
      return `INV-${lines[i + 2]}`;
    }
  }
  const accept = (value) => /^[A-Z0-9][A-Z0-9/-]{1,30}$/i.test(value) && /\d/.test(value) && !DATE_LINE.test(value);
  return nextTo(lines, label, accept, { bareInline: true });
}

function readInvoiceDate(lines) {
  const found = nextTo(lines, /(?:invoice\s+)?date(?:\s+(?:of\s+invoice|issued))?|issue\s+date/, (value) => DATE_LINE.test(value), { bareInline: true });
  if (found) return isoDate(found);
  const first = lines.find((line) => DATE_LINE.test(line));
  return first ? isoDate(first) : "";
}

// ---- The provider ----

// The supplier is the business that sent the bill: not the firm it is addressed to, not an
// address or a label. Where the invoice says who to pay, that is the name to use; otherwise a
// line naming a profession is preferred over the first plain one.
const PROFESSION = /psycholog|psychiatr|neuropsych|medical|doctor|\bdr\.?\s|clinic|health|hospital|interpret|translat|transcri|report|pty\.? ?ltd|consult|services|forensic/i;
const ONLY_A_PROFESSION = /^(?:psychologists?|psychological services|psychology|psychiatry|consultant psychologist|clinical psychologist)$/i;

// "HEALEY & CIDONI PSYCHOLOGISTS" -> "Healey & Cidoni Psychologists". Mixed case is left alone.
const titleCase = (value) => (value !== value.toUpperCase() ? value
  : value.toLowerCase().replace(/(^|[\s&/(-])([a-z])/g, (_, before, letter) => before + letter.toUpperCase()));

const isSupplierLine = (line) =>
  /[A-Za-z]{3}/.test(line)
  && line.length <= 80
  && !LABEL_LINE.test(line)
  && !FIRM_NAMES.test(line)
  && !DATE_LINE.test(line)
  && !/^(?:po box|p\.o\.|tel|ph|phone|fax|email|abn|a\.b\.n|acn|bsb|acct|account|www\.|http|provider no|\d)/i.test(line)
  && !/\b(?:vic|nsw|qld|sa|wa|tas|act|nt)?\s*\d{4}$/i.test(line)
  && !/@/.test(line);

function readSupplier(lines) {
  // "Please make cheque payable to: Jeffrey Example" / "Psychological Services Pty Ltd",
  // "Cheque payable to Carla Example Psychology or EFTPOS", "Account Name: Keystone Example".
  for (let i = 0; i < lines.length; i += 1) {
    const payable = lines[i].match(/(?:payable to|account name)\s*:?\s*(.+)$/i);
    if (!payable) continue;
    let name = payable[1].trim();
    const next = lines[i + 1] || "";
    // The name runs onto the next line: "... payable to Next Door" / "Psychology or bank account EFT".
    if (!/\s+or\s+/i.test(name) && /^[A-Z]/.test(next) && /\b(?:pty|ltd|services|psycholog\w*|group|practice)\b/i.test(next) && !FIRM_NAMES.test(next)) {
      name = `${name} ${next}`;
    }
    name = name.replace(/\s+or\s+.*$/i, "").trim();
    if (/[A-Za-z]{3}/.test(name) && !FIRM_NAMES.test(name) && name.length <= 80) return titleCase(name);
  }
  const candidates = lines.filter(isSupplierLine);
  const index = candidates.findIndex((line) => PROFESSION.test(line) && !/assessment|^report\b/i.test(line));
  if (index < 0) return titleCase(candidates[0] || "");
  // A name printed over two lines: "HEALEY & CIDONI" then "PSYCHOLOGISTS".
  if (ONLY_A_PROFESSION.test(candidates[index]) && index > 0 && lines.indexOf(candidates[index]) - lines.indexOf(candidates[index - 1]) === 1) {
    return titleCase(`${candidates[index - 1]} ${candidates[index]}`);
  }
  return titleCase(candidates[index]);
}

// ---- What was supplied ----

// The column headings of an invoice's table, skipped when looking for its first line.
const TABLE_HEADING = /^(?:description|qty|quantity|unit price|price|rate|tax|gst|amount(?:\s+aud)?|total|hours|item)$/i;

// The invoice's table: its first item after the "Description" heading, with the lines that
// continue it, less a leading item code ("00004, Supplementary Report").
function readDescription(lines, supplier = "") {
  const start = lines.findIndex((line) => /^description$/i.test(line) || /^description of services?$/i.test(line));
  if (start >= 0) {
    const rest = lines.slice(start + 1, start + 16);
    const first = rest.findIndex((line) => !TABLE_HEADING.test(line) && !LABEL_LINE.test(line) && !DATE_LINE.test(line) && !MONEY_LINE.test(line) && /[A-Za-z]{3}/.test(line));
    if (first >= 0) {
      const parts = [rest[first]];
      for (const line of rest.slice(first + 1)) {
        if (MONEY_LINE.test(line) || DATE_LINE.test(line) || LABEL_LINE.test(line) || TABLE_HEADING.test(line)) break;
        if (/^(?:sub\s*)?total\b|^(?:gst|amount due|balance|includes gst)\b/i.test(line)) break;
        parts.push(line);
      }
      return parts.join(" ").replace(/^\d{3,},?\s+/, "").slice(0, 160).trim();
    }
  }
  const supplierText = String(supplier).toLowerCase();
  return lines.find((line) => /\b(?:assessment|report|consultation|interview|interpret\w*|transcri\w*)\b/i.test(line)
    && line.toLowerCase() !== supplierText
    && !ONLY_A_PROFESSION.test(line)
    && !/payable|remit|^(?:re|your ref)\b/i.test(line)
    && line.length <= 200)?.slice(0, 160) || "";
}

export function guessDisbursementType(raw = "") {
  const t = String(raw);
  if (/neuro\s*-?psycholog/i.test(t)) return "neuropsychological_report";
  if (/psychiatr/i.test(t)) return "psychiatric_report";
  if (/psycholog/i.test(t)) return "psychological_report";
  if (/interpret|translat/i.test(t)) return "interpreter";
  if (/transcri/i.test(t)) return "transcript";
  if (/medical|general practi|\bGP\b|hospital|clinic|\bMBBS\b|\bFRAC/i.test(t)) return "medical_report";
  return "other";
}

const toNumber = (raw) => Number(String(raw).replace(/[$,\s]/g, ""));

// The total and the GST in it. The shared reader (src/lib/invoiceAmount.js) is tried first;
// a report writer's invoice often prints the GST apart from its label, so failing that the
// total is the figure on the page eleven times another figure - never arithmetic alone. Where
// the GST is not exactly a tenth (a typed letter's "GST 67.00" on $746), the figure printed
// against "GST" is used.
function readAmounts(raw) {
  const figures = [...String(raw).matchAll(/\$?\s*(\d{1,3}(?:,\d{3})+\.\d{2}|\d+\.\d{2})/g)]
    .map((m) => toNumber(m[1]))
    .filter((n) => Number.isFinite(n) && n > 0 && n < 100000);
  let amount = readInvoiceAmount(raw);
  let gst = "";
  const pairs = [];
  for (const g of figures) {
    for (const t of figures) {
      if (t > g && Math.abs(g * 11 - t) <= 0.1) pairs.push({ g, t });
    }
  }
  if (amount) {
    const match = pairs.find((pair) => Math.abs(pair.t - Number(amount)) <= 0.01);
    if (match) gst = match.g.toFixed(2);
  } else if (pairs.length) {
    const best = pairs.reduce((a, b) => (b.t > a.t ? b : a));
    amount = best.t.toFixed(2);
    gst = best.g.toFixed(2);
  }
  if (!gst) {
    const flat = cleanLines(raw).join("\n");
    const labelled = flat.match(/^(?:gst(?:\s+amount)?|total\s+gst[^\n\d]*?|includes\s+gst[^\n]*?\bof)\s*[:\n]\s*\$?(\d[\d,]*\.\d{2})$/im)
      || flat.match(/\bincludes\s+gst\s+\d+%\s+of\s+\$?(\d[\d,]*\.\d{2})/i);
    const figure = labelled ? toNumber(labelled[1]) : 0;
    if (figure > 0 && (!amount || figure < Number(amount) / 2)) gst = figure.toFixed(2);
  }
  return { amount, gst };
}

export function parseDisbursementText(raw = "") {
  const lines = cleanLines(raw);
  const base = parseInvoiceText(raw);
  const { amount, gst } = readAmounts(raw);
  const supplier = readSupplier(lines);
  let invoiceRef = readInvoiceRef(lines) || base.invoiceRef || "";
  // "13227" read from a reference that also says "INV-13227": keep the number as printed.
  if (/^\d+$/.test(invoiceRef) && new RegExp(`\\bINV-${invoiceRef}\\b`, "i").test(lines.join(" "))) invoiceRef = `INV-${invoiceRef}`;
  const clientName = readClient(lines) || (isNameLike(base.clientName || "") ? base.clientName : "");
  return {
    supplier,
    clientName,
    invoiceRef,
    invoiceDate: readInvoiceDate(lines) || base.invoiceDate || "",
    description: readDescription(lines, supplier),
    amount: amount || base.amount || "",
    gst,
    aidRef: readAidRef(lines),
    type: guessDisbursementType(`${supplier}\n${raw}`),
  };
}

// One PDF can hold several invoices - a supplier often sends a month's invoices together, one
// to a page. When at least two pages each carry their own invoice number and amount, every
// such page is its own invoice, and a page without one is a continuation of the one before.
// Otherwise the whole PDF is one invoice.
export function splitPdfIntoInvoices(pageTexts = []) {
  const pages = pageTexts.map((pageText, index) => ({ page: index + 1, text: String(pageText || ""), found: parseDisbursementText(pageText) }));
  const standalone = pages.filter((page) => page.found.invoiceRef && page.found.amount);
  const distinctRefs = new Set(standalone.map((page) => page.found.invoiceRef));
  if (pages.length < 2 || standalone.length < 2 || distinctRefs.size < 2) {
    const whole = pageTexts.join("\n");
    return [{ page: 1, pageCount: pages.length || 1, ...parseDisbursementText(whole) }];
  }
  const invoices = [];
  for (const page of pages) {
    const starts = page.found.invoiceRef && page.found.amount
      && !(invoices.length && invoices[invoices.length - 1].invoiceRef === page.found.invoiceRef);
    if (starts || !invoices.length) {
      invoices.push({ page: page.page, pageCount: 1, ...page.found });
    } else {
      invoices[invoices.length - 1].pageCount += 1;
    }
  }
  return invoices;
}

// ---- Bringing in disbursements from email -------------------------------------------------
//
// The email intake (api/cron/email-intake.js) files every PDF that arrives in the firms'
// inboxes. Disbursement invoices land there unsorted ("check type") among counsel's bills, so
// they are picked out by what the email and file are called. Each is offered once: adding it
// or marking it "not a disbursement" takes it off the list. The same invoice is often
// forwarded several times, so copies are grouped and shown once.

const DISBURSEMENT_WORDS = /disburs|psycholog|psychiatr|neuro\s*-?psych|medical report|medico|interpret|translat|transcri|auslan|forensic|report invoice|assessment/i;
// A file that is clearly not a bill: a quote, a letter, the report itself, a form.
const NOT_AN_INVOICE = /quote|\bqu\d|\brfq\b|remittance|letter|memo|worksheet|agreement|terms|authority|summary|confirmation|declaration|\bform\b|appointment|guideline|report(?!.*invoice)/i;
const LOOKS_LIKE_INVOICE = /invoice|\binv[-_\s]?\d|\bbill\b/i;

const invoiceNumberIn = (value) => String(value || "").match(/\bINV[-_\s]?(\d{3,})/i)?.[1] || "";

export function isDisbursementEmail(item = {}) {
  if (!item?.id || !item.file_path || item.file_purged_at) return false;
  if (item.kind === "disbursement") return true;
  return item.kind === "check_type" && DISBURSEMENT_WORDS.test(`${item.subject || ""} ${item.file_name || ""}`);
}

export function looksLikeInvoiceFile(item = {}) {
  const name = String(item.file_name || "");
  if (NOT_AN_INVOICE.test(name)) return false;
  return LOOKS_LIKE_INVOICE.test(name) || Number(item.amount) > 0;
}

// The intake names an unsorted email after its subject, so only something shaped like a
// person's name is kept; the PDF usually says whose invoice it is.
const plainName = (value) => {
  const name = String(value || "").trim();
  return name.length <= 40 && !/\d/.test(name) && name.split(/\s+/).length <= 4 && !/invoice|request|report|assessment|disburs|from|re:|fw/i.test(name) ? name : "";
};

export const emailFileUrl = (item = {}) => (item.file_path ? `/api/files?path=${encodeURIComponent(item.file_path)}` : "");

export function emailDisbursementCandidates(emailItems = [], data = emptyDisbursementData()) {
  const taken = new Set(data.items.flatMap((item) => item.emailIds || []));
  const ignored = new Set(data.ignored || []);
  const groups = new Map();
  for (const item of emailItems) {
    if (!isDisbursementEmail(item) || taken.has(item.id)) continue;
    const number = String(item.invoice_ref || "").trim() || invoiceNumberIn(item.file_name) || invoiceNumberIn(item.subject);
    const key = number ? `ref:${number.toLowerCase()}` : `file:${String(item.file_name || "").toLowerCase()}|${item.amount ?? ""}`;
    groups.set(key, [...(groups.get(key) || []), item]);
  }
  const candidates = [];
  for (const [key, list] of groups) {
    if (ignored.has(`email:${key}`)) continue;
    // Newest first; the copy with an amount read off it is the one shown.
    const sorted = [...list].sort((a, b) => String(b.received_at || "").localeCompare(String(a.received_at || "")));
    const primary = sorted.find((item) => Number(item.amount) > 0) || sorted[0];
    const sortedByHand = sorted.some((item) => item.kind === "disbursement");
    candidates.push({
      key: `email:${key}`,
      emailIds: sorted.map((item) => item.id),
      copies: sorted.length,
      subject: String(primary.subject || ""),
      sender: String(primary.sender || ""),
      receivedAt: String(primary.received_at || ""),
      fileName: String(primary.file_name || ""),
      fileUrl: emailFileUrl(primary),
      filePath: String(primary.file_path || ""),
      amount: Number(primary.amount) > 0 ? Number(primary.amount).toFixed(2) : "",
      invoiceRef: String(primary.invoice_ref || "").trim() || invoiceNumberIn(primary.file_name) || "",
      clientName: plainName(emailClientName(primary)),
      likelyInvoice: sortedByHand || looksLikeInvoiceFile(primary),
    });
  }
  return candidates.sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
}

// ---- Bringing in the old Client Account's disbursement records ----------------------------
//
// Deadline Guard's Client Account kept disbursement statements as rows under a supplier
// (`/api/disbursements/records`, the latest record per supplier and firm). Only the rows for
// this firm are offered, each once.

const FIRM_PATTERNS = {
  lacw: /\blacw\b|law (?:and|&) advocacy/i,
  vals: /\bvals\b|victorian aboriginal/i,
  eas: /\beas\b/i,
  rga: /\brga\b|greensill/i,
};

function legacyStatus(raw = "") {
  const value = String(raw).toLowerCase();
  if (/part/.test(value)) return "partial_paid";
  if (/\bpaid\b/.test(value) && !/\b(?:not|un)\s*-?paid|unpaid/.test(value)) return "paid";
  return "not_paid";
}

export function legacyDisbursementCandidates(records = [], brand = "", data = emptyDisbursementData()) {
  const pattern = FIRM_PATTERNS[brand];
  if (!pattern) return [];
  const ignored = new Set(data.ignored || []);
  const taken = new Set(data.items.map((item) => item.legacyKey).filter(Boolean));
  const candidates = [];
  for (const record of Array.isArray(records) ? records : []) {
    if (!pattern.test(`${record?.client_firm || ""} ${record?.client_firm_label || ""}`)) continue;
    const rows = Array.isArray(record?.rows) ? record.rows : [];
    for (const row of rows) {
      const supplier = text(row?.barrister || record.clerk_name || record.firm, 120);
      const amount = parseAmount(row?.outstanding_amount);
      const fields = {
        supplier,
        clientName: text(row?.client_name, 120),
        invoiceRef: text(row?.invoice_number, 60),
        invoiceDate: toIsoDate(row?.issue_date || ""),
        amount: amount ? amount.toFixed(2) : "",
        status: legacyStatus(row?.status),
        note: text([row?.notes, row?.reference_contact].filter(Boolean).join(" · "), 500),
        type: guessDisbursementType(supplier),
      };
      const legacyKey = `legacy:${supplier}|${fields.invoiceRef}|${fields.clientName}`.toLowerCase();
      if (ignored.has(legacyKey) || taken.has(legacyKey)) continue;
      if (fields.invoiceRef && findDuplicate(data.items, fields)) continue;
      candidates.push({
        ...fields,
        key: legacyKey,
        legacyKey,
        statementLabel: text(record.statement_label || record.filename, 120),
      });
    }
  }
  return candidates;
}
